// src/components/roads/page/RoadPageHeader.js
// Extracted from RoadWorksPage v4.7 — breadcrumb, title, live status and page actions.
// --- UPDATE: v5.0 — memoised; relative "Updated x ago"; AI/ML chips; Forecast button shows
//             progress; Print + Help menu items now work; responsive action wrap.
import React, { memo } from 'react';
import {
    Badge, Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Button, Flex, Heading, HStack,
    Icon, IconButton, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Spinner, Switch, Text,
    Tooltip, useColorModeValue,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { FiHelpCircle, FiPrinter } from 'react-icons/fi';
import { MdAddRoad, MdBugReport, MdDownload, MdMoreVert, MdRefresh } from 'react-icons/md';
import { FaRoad } from 'react-icons/fa';
import { AIStatusBadge, MotionBox, MotionFlex, PulseIndicator, formatRelative, useNow } from './shared';
import { AiChip, AiForecastIcon } from './AiMlIcons';

const RoadPageHeader = ({
    isMobile, aiStatus, isConnected, autoRefresh, setAutoRefresh, lastRefresh, isLoading,
    onRefresh, onExport, isExporting, onForecast, isForecasting, onAddRoad, onOpenDebug, onOpenHelp,
}) => {
    const headingColor    = useColorModeValue('gray.800','white');
    const mutedColor      = useColorModeValue('gray.500','gray.400');
    const borderColor     = useColorModeValue('gray.200','gray.700');
    const breadcrumbColor = useColorModeValue('gray.500','gray.400');
    const autoBadgeCardBg = useColorModeValue('white','gray.800');
    const headerGradient  = useColorModeValue('linear(to-r, blue.600, cyan.600)', 'linear(to-r, blue.400, cyan.500)');
    const now = useNow(15000);

    return (
        <MotionFlex initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }}
            transition={{ duration:0.45 }} direction="column" mb={6}>
            <Breadcrumb spacing="8px" separator={<ChevronRightIcon color={breadcrumbColor} />} fontSize="sm" mb={4}>
                <BreadcrumbItem>
                    <BreadcrumbLink href="/admin" color={breadcrumbColor} _hover={{ color:'blue.500' }}>Admin</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink color={headingColor} fontWeight="600">Road Infrastructure</BreadcrumbLink>
                </BreadcrumbItem>
            </Breadcrumb>

            <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                <HStack spacing={4}>
                    <MotionBox initial={{ scale:0 }} animate={{ scale:1, rotate:[0,360] }}
                        transition={{ duration:0.65, type:'spring' }}
                        bgGradient={headerGradient} p={3} borderRadius="xl"
                        boxShadow="0 8px 24px rgba(66,99,235,0.35)">
                        <Icon as={FaRoad} color="white" boxSize={7} />
                    </MotionBox>
                    <Box>
                        <HStack spacing={3} align="center" wrap="wrap">
                            <Heading as="h1" size={isMobile ? 'lg' : 'xl'} fontWeight="900"
                                bgGradient={headerGradient} bgClip="text">
                                Road Infrastructure
                            </Heading>
                            <MotionBox animate={{ y:[0,-3,0] }} transition={{ duration:2, repeat:Infinity, ease:'easeInOut' }}>
                                <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={3} py={1}
                                    fontSize="xs" fontWeight="700" boxShadow="0 0 12px var(--chakra-colors-blue-200)">
                                    FlowSense MOW+
                                </Badge>
                            </MotionBox>
                            <AIStatusBadge status={aiStatus} />
                            {/* --- UPDATE */}
                            <HStack spacing={1} display={{ base:'none', md:'flex' }}>
                                <AiChip kind="ai" size="xs" tooltip="AI insights + predictive maintenance enabled" />
                                <AiChip kind="ml" size="xs" tooltip="RSF · Isolation Forest · DBSCAN models connected" />
                            </HStack>
                            {/* --- END UPDATE */}
                        </HStack>
                        <HStack spacing={3} mt={0.5}>
                            <Text color={mutedColor} fontSize="sm">
                                Public roads · conditions · maintenance · citizen reports
                            </Text>
                            <HStack spacing={1.5} fontSize="xs" color={isConnected ? mutedColor : 'red.400'}>
                                <PulseIndicator color={isConnected ? 'green' : 'red'} size={2} />
                                <Text fontWeight="600">{isConnected ? 'Live' : 'Offline'}</Text>
                            </HStack>
                        </HStack>
                    </Box>
                </HStack>

                <HStack spacing={2} wrap="wrap">
                    <HStack spacing={2} px={3} py={2} borderRadius="xl"
                        borderWidth="1px" borderColor={borderColor} bg={autoBadgeCardBg}
                        _hover={{ borderColor:'green.300' }} transition="all 0.2s">
                        <MotionBox animate={autoRefresh ? { scale:[1,1.3,1] } : {}} transition={{ duration:2, repeat:Infinity }}>
                            <Box w={2} h={2} borderRadius="full" bg={autoRefresh ? 'green.500':'gray.400'}
                                boxShadow={autoRefresh ? '0 0 6px var(--chakra-colors-green-400)':'none'} />
                        </MotionBox>
                        <Text fontSize="xs" fontWeight="600" color={mutedColor}>Auto</Text>
                        <Tooltip label="Enable live websocket updates and 60s polling (paused while the tab is hidden)" hasArrow>
                            <Box display="flex">
                                <Switch size="sm" isChecked={autoRefresh} aria-label="Auto refresh"
                                    onChange={e => setAutoRefresh(e.target.checked)} colorScheme="green" />
                            </Box>
                        </Tooltip>
                    </HStack>
                    <Tooltip label={lastRefresh.toLocaleString()} hasArrow>
                        <Text fontSize="xs" color={mutedColor} display={{ base:'none', lg:'block' }}>
                            Updated {formatRelative(lastRefresh, now)}
                        </Text>
                    </Tooltip>
                    <Tooltip label="Force Data Refresh" hasArrow>
                        <MotionBox whileHover={{ scale:1.06, rotate:180 }} whileTap={{ scale:0.95 }} transition={{ duration:0.3 }}>
                            <IconButton aria-label="Refresh"
                                icon={isLoading ? <Spinner size="sm" /> : <Icon as={MdRefresh} />}
                                size="md" onClick={() => onRefresh()} isLoading={isLoading}
                                variant="ghost" colorScheme="blue" borderRadius="full" />
                        </MotionBox>
                    </Tooltip>
                    <Button leftIcon={<Icon as={MdDownload} />} variant="outline"
                        onClick={onExport} isLoading={isExporting} size="md" borderRadius="xl">
                        Export CSV
                    </Button>
                    <Tooltip label="Run Random Survival Forest prediction against all active road segments" hasArrow>
                        <Button leftIcon={<Icon as={AiForecastIcon} />} colorScheme="purple" size="md" borderRadius="xl"
                            _hover={{ transform:'translateY(-2px)', boxShadow:'0 8px 20px var(--chakra-colors-purple-300)' }}
                            transition="all 0.2s" onClick={onForecast}
                            isLoading={isForecasting} loadingText="Forecasting…">
                            Forecast Risk
                        </Button>
                    </Tooltip>
                    <Button leftIcon={<Icon as={MdAddRoad} />} colorScheme="blue" size="md" borderRadius="xl"
                        _hover={{ transform:'translateY(-2px)', boxShadow:'0 8px 20px var(--chakra-colors-blue-300)' }}
                        transition="all 0.2s" onClick={onAddRoad}>
                        Add Road
                    </Button>
                    {process.env.NODE_ENV === 'development' && (
                        <Tooltip label="Debug Panel" hasArrow>
                            <IconButton aria-label="Debug panel" icon={<Icon as={MdBugReport} />} size="md"
                                onClick={onOpenDebug} variant="ghost" colorScheme="yellow" borderRadius="full" />
                        </Tooltip>
                    )}
                    <Menu>
                        <MenuButton as={IconButton} aria-label="More actions" icon={<Icon as={MdMoreVert} />}
                            size="md" variant="ghost" borderRadius="full" />
                        <MenuList borderRadius="xl" boxShadow="xl">
                            <MenuItem icon={<Icon as={MdDownload} />} onClick={onExport} borderRadius="md">
                                Export Roads CSV
                            </MenuItem>
                            <MenuItem icon={<FiPrinter />} borderRadius="md" onClick={() => window.print()}>
                                Print Report
                            </MenuItem>
                            <MenuDivider />
                            <MenuItem icon={<FiHelpCircle />} borderRadius="md" onClick={onOpenHelp}>
                                Help & Docs
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </HStack>
            </Flex>
        </MotionFlex>
    );
};

export default memo(RoadPageHeader);
